import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import type { CoachInsight } from './coachInsightTypes';
import { useCoachInsights } from '@/hooks/useCoachInsights';

const STORAGE_KEY = 'meq_coach_bar';
const SESSION_WELCOME_KEY = 'meq_coach_welcome_seen';
const MAX_SHOWS_PER_DAY = 5;
const DISMISS_COOLDOWN_MS = 2 * 60 * 60 * 1000; // 2h (explicit dismiss only)

interface DismissState {
  dismissedAt: number;
  showCountToday: number;
  showCountDate: string; // YYYY-MM-DD
}

function getState(): DismissState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* noop */ }
  return { dismissedAt: 0, showCountToday: 0, showCountDate: '' };
}

function setState(state: DismissState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

interface CoachBarContextValue {
  currentInsight: CoachInsight | null;
  dismiss: () => void;
  autoDismiss: () => void;
  isVisible: boolean;
}

const CoachBarContext = createContext<CoachBarContextValue>({
  currentInsight: null,
  dismiss: () => {},
  autoDismiss: () => {},
  isVisible: false,
});

export const useCoachBar = () => useContext(CoachBarContext);

export const CoachBarProvider = ({ children }: { children: ReactNode }) => {
  const { topInsight } = useCoachInsights();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(() => {
    const s = getState();
    return Date.now() - s.dismissedAt < DISMISS_COOLDOWN_MS;
  });

  // Track whether returning_welcome was already shown this browser session
  const [welcomeSeenThisSession] = useState(() => {
    try { return sessionStorage.getItem(SESSION_WELCOME_KEY) === 'true'; }
    catch { return false; }
  });

  // 2-second appearance delay on mount and route changes
  const [delayedReady, setDelayedReady] = useState(false);
  useEffect(() => {
    setDelayedReady(false);
    const timer = setTimeout(() => setDelayedReady(true), 2000);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Track whether we've already counted the current showing session
  const hasCountedRef = useRef(false);

  const today = todayStr();
  const state = getState();
  const showCount = state.showCountDate === today ? state.showCountToday : 0;
  const rateLimited = showCount >= MAX_SHOWS_PER_DAY;

  // Persistent insights (onboarding states) bypass dismiss and rate limits
  const isPersistent = topInsight?.persistent === true;
  const isWelcomeBlocked = topInsight?.type === 'returning_welcome' && welcomeSeenThisSession;
  const isVisible = delayedReady && !isWelcomeBlocked && (isPersistent
    ? !!topInsight
    : !!topInsight && !dismissed && !rateLimited);

  // Track show count via effect (runs once per visibility transition, not per render)
  useEffect(() => {
    if (isVisible && !isPersistent && !hasCountedRef.current) {
      hasCountedRef.current = true;
      const s = getState();
      setState({
        ...s,
        showCountToday: s.showCountDate === today ? s.showCountToday + 1 : 1,
        showCountDate: today,
      });
    }
    if (!isVisible) {
      hasCountedRef.current = false;
    }
  }, [isVisible, isPersistent, today]);

  const dismiss = useCallback(() => {
    // Don't allow dismissing persistent onboarding insights
    if (topInsight?.persistent) return;
    const now = Date.now();
    setState({ ...getState(), dismissedAt: now });
    setDismissed(true);
    // Mark returning_welcome as seen for this session (prevents re-show on route changes)
    try { sessionStorage.setItem(SESSION_WELCOME_KEY, 'true'); } catch { /* noop */ }
  }, [topInsight]);

  // Auto-dismiss: hides the current insight without triggering the cooldown timer.
  // Used by the 12-second auto-dismiss so the bar can re-appear with new insights.
  const autoDismiss = useCallback(() => {
    if (topInsight?.persistent) return;
    // Mark returning_welcome as seen for this session
    if (topInsight?.type === 'returning_welcome') {
      try { sessionStorage.setItem(SESSION_WELCOME_KEY, 'true'); } catch { /* noop */ }
    }
    // Increment show count but do NOT set dismissedAt — no cooldown
    const s = getState();
    const today = todayStr();
    setState({
      ...s,
      showCountToday: s.showCountDate === today ? s.showCountToday + 1 : 1,
      showCountDate: today,
    });
    // Force a re-render to hide the current insight temporarily
    setDismissed(true);
    // Re-enable after a short delay so the next insight can appear on route change
    setTimeout(() => setDismissed(false), 3000);
  }, [topInsight]);

  return (
    <CoachBarContext.Provider value={{ currentInsight: topInsight, dismiss, autoDismiss, isVisible }}>
      {children}
    </CoachBarContext.Provider>
  );
};
